/**
 * ORSENZA — interacciones y motion del tema.
 * Vanilla JS, sin dependencias. Web Components + módulos de scroll.
 * Todo respeta prefers-reduced-motion y degrada sin JS.
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const FINE_POINTER = window.matchMedia('(pointer: fine)').matches;

/**
 * Formatea céntimos como moneda en es-ES.
 * @param {number} cents
 * @param {string} [currency]
 * @returns {string}
 */
const formatMoney = (cents, currency = 'EUR') => {
  try {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} €`;
  }
};

/**
 * Crea un elemento con clase y texto (texto siempre via textContent — sin XSS).
 * @param {string} tag
 * @param {string} [className]
 * @param {string} [text]
 * @returns {HTMLElement}
 */
const el = (tag, className, text) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
};

/**
 * ¿El clic ha caído en el ::backdrop (fuera de la caja del diálogo)?
 * @param {HTMLDialogElement} dialog
 * @param {MouseEvent} event
 * @returns {boolean}
 */
const isBackdropClick = (dialog, event) => {
  const rect = dialog.getBoundingClientRect();
  return event.clientX < rect.left || event.clientX > rect.right
    || event.clientY < rect.top || event.clientY > rect.bottom;
};

/**
 * Redimensiona una URL de imagen del CDN de Shopify.
 * @param {string} src
 * @param {number} width
 * @returns {string}
 */
const imageSize = (src, width) => {
  try {
    const url = new URL(src, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.toString();
  } catch {
    return src;
  }
};

/**
 * Favoritos — almacenados como array de handles en localStorage.
 * Un único evento `wishlist:change` en document sincroniza todas las
 * instancias de <favorite-button>, el contador del header y la página
 * de favoritos entre sí, sin acoplarlas.
 */
const WISHLIST_KEY = 'orsenza-wishlist';

const getWishlist = () => {
  try {
    const raw = localStorage.getItem(WISHLIST_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
};

const setWishlist = (handles) => {
  try {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(handles));
  } catch {
    /* almacenamiento no disponible (privado/lleno) — se degrada sin persistencia */
  }
  document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { handles } }));
};

const toggleWishlist = (handle) => {
  const list = getWishlist();
  const index = list.indexOf(handle);
  if (index > -1) {
    list.splice(index, 1);
  } else {
    list.push(handle);
  }
  setWishlist(list);
  return index === -1;
};

/* ── <quantity-input> — selector de cantidad accesible ───────────── */
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    for (const button of this.querySelectorAll('button')) {
      button.addEventListener('click', () => this.#step(button.dataset.step === 'up' ? 1 : -1));
    }
  }

  #step(delta) {
    if (!this.input) return;
    const min = Number(this.input.min || 1);
    const value = Math.max(min, (parseInt(this.input.value, 10) || min) + delta);
    if (String(value) === this.input.value) return;
    this.input.value = String(value);
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}
customElements.define('quantity-input', QuantityInput);

/* ── <announcement-bar> — mensajes rotativos ─────────────────────── */
class AnnouncementBar extends HTMLElement {
  #timer = null;
  #index = 0;

  connectedCallback() {
    this.messages = [...this.querySelectorAll('.announcement-bar__message')];
    if (this.messages.length < 2 || REDUCED_MOTION) return;
    this.addEventListener('mouseenter', () => this.#stop());
    this.addEventListener('mouseleave', () => this.#start());
    this.addEventListener('focusin', () => this.#stop());
    this.addEventListener('focusout', () => this.#start());
    this.#start();
  }

  disconnectedCallback() { this.#stop(); }

  #start() {
    if (this.#timer) return;
    this.#timer = setInterval(() => this.#next(), 5000);
  }

  #stop() {
    clearInterval(this.#timer);
    this.#timer = null;
  }

  #next() {
    this.messages[this.#index].classList.remove('announcement-bar__message--current');
    this.#index = (this.#index + 1) % this.messages.length;
    this.messages[this.#index].classList.add('announcement-bar__message--current');
  }
}
customElements.define('announcement-bar', AnnouncementBar);

/* ── <marquee-band> — cinta con botón de pausa (WCAG 2.2.2) ──────── */
class MarqueeBand extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('.marquee__toggle');
    if (!this.toggle) return;
    // sin JS (o con movimiento reducido) la cinta no se mueve: el botón sobra
    if (REDUCED_MOTION) return;
    this.toggle.hidden = false;
    this.toggle.addEventListener('click', () => {
      const paused = this.classList.toggle('marquee--paused');
      this.toggle.setAttribute('aria-pressed', String(paused));
    });
  }
}
customElements.define('marquee-band', MarqueeBand);

/* ── <cart-drawer> — carrito lateral con upsell ──────────────────── */
class CartDrawer extends HTMLElement {
  #controller = null;

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
    this.itemsList = this.querySelector('[data-drawer-items]');
    this.emptyState = this.querySelector('[data-drawer-empty]');
    this.footer = this.querySelector('[data-drawer-footer]');
    this.subtotal = this.querySelector('[data-drawer-subtotal]');
    this.errorRegion = this.querySelector('[data-drawer-error]');
    this.shippingText = this.querySelector('[data-shipping-text]');
    this.shippingFill = this.querySelector('[data-shipping-fill]');
    this.upsellBlock = this.querySelector('[data-drawer-upsell]');
    this.currency = this.dataset.currency || 'EUR';
    this.threshold = Number(this.dataset.threshold || 0);
    this.strings = this.#readJSON('[data-drawer-strings]') || {};
    this.upsellPool = this.#readJSON('[data-drawer-upsell-data]') || [];

    this.querySelector('.cart-drawer__close')?.addEventListener('click', () => this.close());
    this.dialog.addEventListener('click', (event) => {
      if (isBackdropClick(this.dialog, event)) this.close();
    });
    document.addEventListener('cart:updated', (event) => this.#render(event.detail));

    // los enlaces al carrito abren el drawer sin recargar (mejora progresiva)
    for (const link of document.querySelectorAll('[data-cart-link]')) {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        this.open();
      });
    }
  }

  async open() {
    if (!this.dialog.open) this.dialog.showModal();
    await this.refresh();
  }

  close() { this.dialog.close(); }

  async refresh() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    try {
      const response = await fetch('/cart.js', { signal: this.#controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const cart = await response.json();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    } catch (error) {
      if (error.name !== 'AbortError') this.#showError();
    }
  }

  /** @param {number|string} key línea del carrito @param {number} quantity */
  async changeLine(key, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: await response.json() }));
    } catch {
      this.#showError();
    }
  }

  /** @param {number} variantId */
  async addVariant(variantId) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      await this.refresh();
    } catch {
      this.#showError();
    }
  }

  #readJSON(selector) {
    const node = this.querySelector(selector);
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch { return null; }
  }

  #showError() {
    if (this.errorRegion) this.errorRegion.textContent = this.strings.error || 'Error';
  }

  #render(cart) {
    if (!this.itemsList) return;
    if (this.errorRegion) this.errorRegion.textContent = '';
    this.itemsList.replaceChildren();

    const hasItems = cart.item_count > 0;
    if (this.emptyState) this.emptyState.hidden = hasItems;
    if (this.footer) this.footer.hidden = !hasItems;

    for (const item of cart.items) this.itemsList.append(this.#renderLine(item));
    if (this.subtotal) this.subtotal.textContent = formatMoney(cart.items_subtotal_price, this.currency);
    this.#renderShipping(cart);
    this.#renderUpsell(cart);
  }

  #renderLine(item) {
    const li = el('li', 'line-item');

    const media = el('div', 'line-item__media');
    if (item.image) {
      const img = el('img', 'line-item__image');
      img.src = imageSize(item.image, 112);
      img.alt = '';
      img.width = 56;
      img.loading = 'lazy';
      media.append(img);
    } else {
      media.append(el('div', 'bottle bottle--small'));
    }

    const body = el('div', 'line-item__body');
    const title = el('a', 'line-item__title', item.product_title);
    title.href = item.url;
    body.append(title);
    if (item.variant_title && item.variant_title !== 'Default Title') {
      body.append(el('p', 'line-item__meta', item.variant_title));
    }

    const controls = el('div', 'line-item__controls');
    const quantity = el('quantity-input', 'quantity');
    const minus = el('button', 'quantity__button', '−');
    minus.type = 'button';
    minus.dataset.step = 'down';
    minus.setAttribute('aria-label', this.strings.decrease || 'Reducir cantidad');
    const input = el('input', 'quantity__input');
    input.type = 'number';
    input.min = '0';
    input.value = String(item.quantity);
    input.setAttribute('aria-label', `${this.strings.quantity || 'Cantidad'} — ${item.product_title}`);
    const plus = el('button', 'quantity__button', '+');
    plus.type = 'button';
    plus.dataset.step = 'up';
    plus.setAttribute('aria-label', this.strings.increase || 'Aumentar cantidad');
    quantity.append(minus, input, plus);
    input.addEventListener('change', () => this.changeLine(item.key, Math.max(0, parseInt(input.value, 10) || 0)));

    const remove = el('button', 'line-item__remove', this.strings.remove || 'Quitar');
    remove.type = 'button';
    remove.setAttribute('aria-label', `${this.strings.remove || 'Quitar'} — ${item.product_title}`);
    remove.addEventListener('click', () => this.changeLine(item.key, 0));
    controls.append(quantity, remove);
    body.append(controls);

    const price = el('p', 'line-item__price', formatMoney(item.final_line_price, this.currency));
    li.append(media, body, price);
    return li;
  }

  #renderShipping(cart) {
    if (!this.shippingText || !this.shippingFill || !this.threshold) return;
    const wrapper = this.shippingText.closest('.cart-drawer__shipping');
    if (wrapper) wrapper.hidden = cart.item_count === 0;
    if (cart.total_price >= this.threshold) {
      this.shippingText.innerHTML = `<strong>${this.strings.freeShippingReached || ''}</strong>`;
      this.shippingFill.style.width = '100%';
    } else {
      const remaining = formatMoney(this.threshold - cart.total_price, this.currency);
      const template = this.strings.freeShippingRemaining || '';
      this.shippingText.innerHTML = template.replace('[amount]', `<strong>${remaining}</strong>`);
      this.shippingFill.style.width = `${Math.round((cart.total_price / this.threshold) * 100)}%`;
    }
  }

  #renderUpsell(cart) {
    if (!this.upsellBlock) return;
    const inCart = new Set(cart.items.map((item) => item.product_id));
    const candidate = this.upsellPool.find((product) => !inCart.has(product.product_id));
    const show = cart.item_count > 0 && Boolean(candidate);
    this.upsellBlock.hidden = !show;
    if (!show) return;

    const card = this.upsellBlock.querySelector('[data-upsell-card]');
    card.replaceChildren();
    const media = el('div', 'upsell-card__media');
    if (candidate.image) {
      const img = el('img', 'upsell-card__image');
      img.src = candidate.image;
      img.alt = '';
      img.width = 44;
      img.loading = 'lazy';
      media.append(img);
    } else {
      media.append(el('div', 'bottle bottle--small'));
    }
    const body = el('div', 'upsell-card__body');
    body.append(el('p', 'upsell-card__title', candidate.title));
    body.append(el('p', 'upsell-card__price', formatMoney(candidate.price, this.currency)));
    const add = el('button', 'btn btn--small', this.strings.upsellAdd || 'Añadir');
    add.type = 'button';
    add.setAttribute('aria-label', `${this.strings.upsellAdd || 'Añadir'} ${candidate.title}`);
    add.addEventListener('click', () => this.addVariant(candidate.variant_id));
    card.append(media, body, add);
  }
}
customElements.define('cart-drawer', CartDrawer);

/* ── <product-form> — añadir al carrito sin recargar ─────────────── */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.errorRegion = this.querySelector('[data-form-error]');
    const drawer = document.querySelector('cart-drawer');
    if (!this.form || !drawer || !drawer.querySelector('dialog')) return;
    this.form.addEventListener('submit', (event) => this.#submit(event, drawer));
  }

  async #submit(event, drawer) {
    // los botones dinámicos de pago envían el formulario por su cuenta
    if (event.submitter && event.submitter.name === 'checkout') return;
    event.preventDefault();
    const button = this.form.querySelector('[type="submit"]');
    if (this.errorRegion) this.errorRegion.textContent = '';
    if (button) button.disabled = true;
    try {
      const response = await fetch('/cart/add.js', { method: 'POST', body: new FormData(this.form) });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.description || 'add-error');
      }
      await drawer.open();
    } catch {
      if (this.errorRegion) this.errorRegion.textContent = this.dataset.errorMessage || 'Error';
    } finally {
      if (button) button.disabled = false;
    }
  }
}
customElements.define('product-form', ProductForm);

/* ── <predictive-search> — búsqueda con la API de Shopify ────────── */
class PredictiveSearch extends HTMLElement {
  #controller = null;
  #debounce = null;

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
    this.input = this.querySelector('.search-modal__input');
    this.list = this.querySelector('[data-search-results]');
    this.status = this.querySelector('[data-search-status]');
    this.viewAll = this.querySelector('[data-search-view-all]');
    this.currency = this.dataset.currency || 'EUR';

    for (const trigger of document.querySelectorAll('[data-search-open]')) {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.dialog.showModal();
        this.input?.focus();
      });
    }
    this.querySelector('.search-modal__close')?.addEventListener('click', () => this.dialog.close());
    this.dialog.addEventListener('click', (event) => {
      if (isBackdropClick(this.dialog, event)) this.dialog.close();
    });
    this.input?.addEventListener('input', () => {
      clearTimeout(this.#debounce);
      this.#debounce = setTimeout(() => this.#search(this.input.value.trim()), 250);
    });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    clearTimeout(this.#debounce);
  }

  async #search(query) {
    this.#controller?.abort();
    if (query.length < 2) {
      this.#renderResults([]);
      if (this.status) this.status.textContent = '';
      if (this.viewAll) this.viewAll.hidden = true;
      return;
    }
    this.#controller = new AbortController();
    const url = new URL('/search/suggest.json', window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '5');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    try {
      const response = await fetch(url, { signal: this.#controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const products = data?.resources?.results?.products || [];
      this.#renderResults(products);
      if (this.status) {
        this.status.textContent = products.length === 1
          ? (this.dataset.oneResult || '1 resultado')
          : (this.dataset.resultsCount || '[count] resultados').replace('[count]', String(products.length));
      }
      if (this.viewAll) {
        this.viewAll.hidden = products.length === 0;
        const target = new URL(this.viewAll.dataset.baseUrl || '/search', window.location.origin);
        target.searchParams.set('q', query);
        this.viewAll.href = `${target.pathname}${target.search}`;
      }
    } catch (error) {
      if (error.name !== 'AbortError' && this.status) {
        this.status.textContent = this.dataset.errorMessage || '';
      }
    }
  }

  #renderResults(products) {
    if (!this.list) return;
    this.list.replaceChildren();
    for (const product of products) {
      const li = el('li', 'search-result');
      const link = el('a', 'search-result__link');
      link.href = product.url;
      const media = el('div', 'search-result__media');
      if (product.featured_image?.url) {
        const img = el('img', 'search-result__image');
        img.src = product.featured_image.url;
        img.alt = '';
        img.width = 44;
        img.loading = 'lazy';
        media.append(img);
      } else {
        media.append(el('div', 'bottle bottle--small'));
      }
      link.append(media, el('p', 'search-result__title', product.title));
      if (product.price) {
        link.append(el('p', 'search-result__price', formatMoney(Math.round(parseFloat(product.price) * 100), this.currency)));
      }
      li.append(link);
      this.list.append(li);
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);

/* ── <exit-intent> — pop-up del Club al intentar salir ───────────── */
/* Una vez por sesión, solo escritorio, nunca antes de 8 s. */
class ExitIntent extends HTMLElement {
  #armed = false;

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
    if (sessionStorage.getItem('orsenza-exit') || !FINE_POINTER) return;
    setTimeout(() => { this.#armed = true; }, 8000);
    this.onMouseOut = (event) => {
      if (this.#armed && !event.relatedTarget && event.clientY <= 0) this.#show();
    };
    document.addEventListener('mouseout', this.onMouseOut);
    this.querySelector('.exit-pop__close')?.addEventListener('click', () => this.dialog.close());
    this.querySelector('.btn')?.addEventListener('click', () => this.dialog.close());
  }

  disconnectedCallback() {
    document.removeEventListener('mouseout', this.onMouseOut);
  }

  #show() {
    sessionStorage.setItem('orsenza-exit', '1');
    document.removeEventListener('mouseout', this.onMouseOut);
    this.dialog.showModal();
  }
}
customElements.define('exit-intent', ExitIntent);

/* ── <image-zoom> — clic o Intro sobre la foto para ampliarla ─────── */
class ImageZoom extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('.image-zoom__modal');
    this.trigger = this.querySelector('.image-zoom__trigger');
    if (!this.dialog || !this.trigger || typeof this.dialog.showModal !== 'function') return;
    this.trigger.addEventListener('click', () => this.dialog.showModal());
    this.querySelector('.image-zoom__close')?.addEventListener('click', () => this.dialog.close());
    this.dialog.addEventListener('click', (event) => {
      if (isBackdropClick(this.dialog, event)) this.dialog.close();
    });
  }
}
customElements.define('image-zoom', ImageZoom);

/* ── <brand-intro> — el monograma antes de la portada ─────────────── */
/* Una vez por sesión, solo en la home, siempre saltable y ausente si */
/* hay movimiento reducido (la propia hoja de estilos ya la oculta,   */
/* esto es la garantía en JS por si el CSS de un navegador antiguo no */
/* llega a cargar a tiempo). */
class BrandIntro extends HTMLElement {
  connectedCallback() {
    if (REDUCED_MOTION || sessionStorage.getItem('orsenza-intro')) {
      this.remove();
      return;
    }
    sessionStorage.setItem('orsenza-intro', '1');

    const finish = () => {
      if (this.classList.contains('brand-intro--leaving')) return;
      this.classList.add('brand-intro--leaving');
      document.removeEventListener('keydown', finish);
      this.addEventListener('animationend', () => this.remove(), { once: true });
      // red de seguridad por si el navegador no dispara animationend
      setTimeout(() => this.remove(), 900);
    };

    this.addEventListener('click', finish);
    document.addEventListener('keydown', finish);
    setTimeout(finish, 1700);
  }
}
customElements.define('brand-intro', BrandIntro);

/* ── <favorite-button> — corazón que guarda/quita de la wishlist ─── */
class FavoriteButton extends HTMLElement {
  connectedCallback() {
    this.handle = this.dataset.handle;
    this.button = this.querySelector('button');
    if (!this.handle || !this.button) return;

    this.#sync();
    this.button.addEventListener('click', () => {
      const active = toggleWishlist(this.handle);
      this.#render(active);
    });

    this.onWishlistChange = () => this.#sync();
    document.addEventListener('wishlist:change', this.onWishlistChange);
  }

  disconnectedCallback() {
    if (this.onWishlistChange) document.removeEventListener('wishlist:change', this.onWishlistChange);
  }

  #sync() {
    this.#render(getWishlist().includes(this.handle));
  }

  #render(active) {
    this.button.setAttribute('aria-pressed', String(active));
    this.button.setAttribute('aria-label', active ? this.button.dataset.labelRemove : this.button.dataset.labelAdd);
    this.classList.toggle('is-active', active);
  }
}
customElements.define('favorite-button', FavoriteButton);

/* ── Contador de favoritos en el header ────────────────────────────── */
(() => {
  const link = document.querySelector('[data-wishlist-link]');
  const countEl = document.querySelector('[data-wishlist-count]');
  if (!link || !countEl) return;
  const update = () => {
    const count = getWishlist().length;
    countEl.textContent = count > 0 ? `(${count})` : '';
    if (link.dataset.wishlistLabel) {
      link.setAttribute('aria-label', link.dataset.wishlistLabel.replace('__COUNT__', String(count)));
    }
    link.classList.toggle('has-favorites', count > 0);
  };
  update();
  document.addEventListener('wishlist:change', update);
})();

/* ── Página de favoritos — hidrata las tarjetas vía /products/{handle}.js ── */
(() => {
  const list = document.querySelector('[data-favorites-list]');
  if (!list) return;
  const emptyState = document.querySelector('[data-favorites-empty]');
  const countEl = document.querySelector('[data-favorites-count]');

  const FAVORITE_ICON_SVG = '<svg class="favorite-button__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">'
    + '<path class="favorite-button__icon-outline" fill="none" stroke="currentColor" stroke-width="1.6" d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>'
    + '<path class="favorite-button__icon-fill" fill="currentColor" d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>'
    + '</svg>';

  const renderCard = (product) => {
    const article = el('article', 'product-card');

    const link = document.createElement('a');
    link.className = 'product-card__link';
    link.href = product.url;

    const media = el('div', 'product-card__media');
    if (product.featured_image) {
      const img = document.createElement('img');
      img.className = 'product-card__image';
      img.src = imageSize(product.featured_image, 560);
      img.alt = product.title;
      img.loading = 'lazy';
      media.append(img);
    }
    link.append(media);
    link.append(el('h3', 'product-card__title', product.title));
    if (product.type) link.append(el('p', 'product-card__family', product.type));
    const price = el('p', 'price product-card__price', formatMoney(product.price));
    link.append(price);
    article.append(link);

    const fav = document.createElement('favorite-button');
    fav.className = 'favorite-button favorite-button--card';
    fav.dataset.handle = product.handle;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'favorite-button__control';
    btn.dataset.labelAdd = list.dataset.labelAdd || '';
    btn.dataset.labelRemove = list.dataset.labelRemove || '';
    btn.innerHTML = FAVORITE_ICON_SVG;
    fav.append(btn);
    article.append(fav);

    const discover = document.createElement('a');
    discover.className = 'btn';
    discover.href = product.url;
    discover.tabIndex = -1;
    discover.textContent = list.dataset.discoverLabel || '';
    article.append(discover);

    return article;
  };

  const load = async () => {
    const handles = getWishlist();

    if (!handles.length) {
      list.hidden = true;
      if (emptyState) emptyState.hidden = false;
      if (countEl) countEl.textContent = '';
      return;
    }

    const results = await Promise.all(handles.map(async (handle) => {
      try {
        const response = await fetch(`/products/${handle}.js`);
        return response.ok ? await response.json() : null;
      } catch {
        return null;
      }
    }));

    const stale = handles.filter((_, index) => results[index] === null);
    if (stale.length) {
      setWishlist(handles.filter((_, index) => results[index] !== null));
      return; // el evento wishlist:change disparado por setWishlist relanza load()
    }

    list.textContent = '';
    for (const product of results) list.append(renderCard(product));
    list.hidden = false;
    if (emptyState) emptyState.hidden = true;
    if (countEl && list.dataset.countTemplate) {
      countEl.textContent = list.dataset.countTemplate.replace('__COUNT__', String(results.length));
    }
  };

  load();
  document.addEventListener('wishlist:change', load);
})();

/* ── Selector de formato (1 ud / 2 uds) → sincroniza la cantidad ── */
document.addEventListener('change', (event) => {
  const option = event.target.closest?.('[data-purchase-option]');
  if (!option) return;
  const quantity = option.closest('form')?.querySelector('input[name="quantity"]');
  if (!quantity) return;
  quantity.value = option.dataset.quantity || '1';
  quantity.dispatchEvent(new Event('change', { bubbles: true }));
});

/* ── Preselección del país en formularios de dirección ───────────── */
for (const select of document.querySelectorAll('select[data-default]')) {
  if (select.dataset.default) select.value = select.dataset.default;
}

/* ── Contador del carrito en la cabecera ─────────────────────────── */
document.addEventListener('cart:updated', (event) => {
  for (const counter of document.querySelectorAll('[data-cart-count]')) {
    counter.textContent = `(${event.detail.item_count})`;
  }
});

/* ── Título del héroe letra a letra (accesible) ──────────────────── */
(() => {
  if (REDUCED_MOTION) return;
  const heading = document.querySelector('.hero__title');
  if (!heading) return;
  const text = heading.textContent.trim();
  heading.replaceChildren();
  const accessible = el('span', 'visually-hidden', text);
  heading.append(accessible);
  [...text].forEach((char, index) => {
    const letter = el('span', 'hero__letter', char);
    letter.setAttribute('aria-hidden', 'true');
    letter.style.animationDelay = `${0.2 + index * 0.06}s`;
    heading.append(letter);
  });
})();

/* ── Aparición escalonada al hacer scroll ────────────────────────── */
(() => {
  if (REDUCED_MOTION || !('IntersectionObserver' in window)) return;
  const targets = document.querySelectorAll('main .section, .product-card, .perk, .pack-banner');
  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target.classList.add('reveal--in');
      observer.unobserve(entry.target);
      // limpia el estado al terminar para no interferir con hover/tilt
      setTimeout(() => {
        entry.target.classList.remove('reveal', 'reveal--in');
        entry.target.style.transitionDelay = '';
      }, 1200);
    }
  }, { threshold: 0.12 });
  for (const target of targets) {
    if (target.classList.contains('product-card') || target.classList.contains('perk')) {
      const siblings = target.parentElement ? [...target.parentElement.children] : [];
      target.style.transitionDelay = `${siblings.indexOf(target) * 90}ms`;
    }
    target.classList.add('reveal');
    observer.observe(target);
  }
})();

/* ── Scroll: parallax del héroe, historia y cabecera oculta ──────── */
(() => {
  if (REDUCED_MOTION) return;
  const header = document.querySelector('.header');
  const heroContent = document.querySelector('.hero__content');
  const storyVisual = document.querySelector('.story__visual');
  let lastY = 0;
  let ticking = false;
  const frame = () => {
    const y = window.scrollY;
    header?.classList.toggle('header--hidden', y > 420 && y > lastY);
    if (heroContent) {
      heroContent.style.transform = `translateY(${y * 0.22}px)`;
      heroContent.style.opacity = String(Math.max(0, 1 - y / 620));
    }
    if (storyVisual) {
      const rect = storyVisual.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < window.innerHeight) {
        const offset = (rect.top + rect.height / 2 - window.innerHeight / 2) * -0.06;
        storyVisual.style.transform = `translateY(${offset.toFixed(1)}px)`;
      }
    }
    lastY = y;
    ticking = false;
  };
  window.addEventListener('scroll', () => {
    if (!ticking) {
      window.requestAnimationFrame(frame);
      ticking = true;
    }
  }, { passive: true });
  frame();
})();

/* ── Tilt 3D sutil en las tarjetas de producto ───────────────────── */
(() => {
  if (REDUCED_MOTION || !FINE_POINTER) return;
  for (const card of document.querySelectorAll('.product-card')) {
    card.addEventListener('mousemove', (event) => {
      const rect = card.getBoundingClientRect();
      const rotateX = ((event.clientY - rect.top) / rect.height - 0.5) * -5;
      const rotateY = ((event.clientX - rect.left) / rect.width - 0.5) * 5;
      card.style.transform =
        `perspective(900px) rotateX(${rotateX.toFixed(2)}deg) rotateY(${rotateY.toFixed(2)}deg) translateY(-6px)`;
    });
    card.addEventListener('mouseleave', () => { card.style.transform = ''; });
  }
})();

/* ── Cursor a medida (lupa) sobre la foto de la ficha de producto ─── */
(() => {
  if (REDUCED_MOTION || !FINE_POINTER) return;
  const targets = document.querySelectorAll('[data-zoom-cursor]');
  if (!targets.length) return;

  const cursor = document.createElement('div');
  cursor.className = 'zoom-cursor';
  cursor.setAttribute('aria-hidden', 'true');
  cursor.innerHTML = '<svg viewBox="0 0 24 24" focusable="false"><circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" stroke-width="1.4"/><line x1="15.3" y1="15.3" x2="21" y2="21" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
  document.body.append(cursor);

  let targetX = 0, targetY = 0, currentX = 0, currentY = 0, active = false, raf = null;

  const render = () => {
    currentX += (targetX - currentX) * 0.22;
    currentY += (targetY - currentY) * 0.22;
    cursor.style.transform = `translate(${currentX}px, ${currentY}px) translate(-50%, -50%)`;
    raf = active ? requestAnimationFrame(render) : null;
  };

  for (const target of targets) {
    target.style.cursor = 'none';
    const trigger = target.querySelector('.image-zoom__trigger');
    if (trigger) trigger.style.cursor = 'none';
    target.addEventListener('mouseenter', (event) => {
      active = true;
      targetX = currentX = event.clientX;
      targetY = currentY = event.clientY;
      cursor.classList.add('zoom-cursor--visible');
      if (!raf) raf = requestAnimationFrame(render);
    });
    target.addEventListener('mousemove', (event) => {
      targetX = event.clientX;
      targetY = event.clientY;
    });
    target.addEventListener('mouseleave', () => {
      active = false;
      cursor.classList.remove('zoom-cursor--visible');
    });
  }
})();

/* ── <the-bottle> — «El frasco», relato cinematográfico ────────────── */
/* Cada capítulo activa un detalle del frasco (vidrio, tapón, color,   */
/* precinto) al entrar en el centro de la pantalla. Sin scroll-jacking: */
/* el usuario controla el scroll en todo momento, esto solo enciende   */
/* un acento visual. Sin JS o con movimiento reducido, el frasco queda */
/* simplemente estático y el texto se lee exactamente igual.           */
class TheBottle extends HTMLElement {
  connectedCallback() {
    this.chapters = [...this.querySelectorAll('[data-bottle-chapter]')];
    if (!this.chapters.length || typeof IntersectionObserver === 'undefined') return;

    for (const swatch of this.querySelectorAll('[data-bottle-color]')) {
      swatch.addEventListener('click', () => this.dataset.bottleColor = swatch.dataset.bottleColor);
    }

    if (REDUCED_MOTION) return; // el frasco queda fijo en su estado inicial; el relato se lee igual

    this.observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) this.dataset.activeChapter = entry.target.dataset.bottleChapter;
      }
    }, { threshold: 0.6 });

    for (const chapter of this.chapters) this.observer.observe(chapter);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}
customElements.define('the-bottle', TheBottle);

/* ── CTA flotante en móvil (solo en la home) ─────────────────────── */
(() => {
  const cta = document.querySelector('.float-cta');
  if (!cta) return;
  window.addEventListener('scroll', () => {
    const nearBottom = window.innerHeight + window.scrollY > document.body.scrollHeight - 400;
    cta.classList.toggle('float-cta--visible', window.scrollY > 600 && !nearBottom);
  }, { passive: true });
})();
